import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Snackbar,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import DatePickerCustom from '../../components/DatePicker/DatePickerCustom';
import { getCroProjectTracker, getProjectDepartmentIncome } from '../../redux/features/croProjectTrackerSlice';
import CroGrid from './components/CroGrid';
import SummaryTable from './components/SummaryTable';
import KpiCards from './components/KpiCards';
import DetailDialog from './components/DetailDialog';
import ProjectTabs from './components/ProjectTabs';
import ProjectDepartmentInfo from './components/ProjectDepartmentInfo';
import ProjectDepartmentTable from './components/ProjectDepartmentTable';
import {
  EXPANDED_STORAGE_KEY,
  hasActivity,
  croCount,
  groupRows,
  nextSort,
  NUMERIC_COLUMNS,
  PANEL_SX,
  PERIODS,
  periodRange,
  projectOrderOf,
  readExpanded,
  sortRows,
  TAR_BANDS,
  THEME,
} from './utils/trackerConfig';

const fmtDay = (d) => dayjs(d).format('D MMM');

// From this viewport width the CRO tracker and the Accounts Manager sit side by side (a 13"/14" MacBook at 100% zoom is 1470-1512px wide);
// below it the Accounts Manager drops under the grid. The CRO tracker gets the bigger share, a little less on the very widest screens.
const SIDE_BY_SIDE = '@media (min-width: 1440px)';
const WIDE_SCREEN = '@media (min-width: 1680px)';

export default function CroProjectTracker() {
  const dispatch = useDispatch();
  const { responseData, loading, error } = useSelector((state) => state.croProjectTracker.tracker);
  const { responseData: deptResponse, loading: deptLoading, error: deptError } = useSelector((state) => state.croProjectTracker.departmentIncome);

  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM-01'));
  const [period, setPeriod] = useState('month'); // 'month' | 'w1'..'w4' - the sheet's Week 1-4
  // Exclusive alternate view (SkillX is a separate company), same as the Department Income Tracker.
  const [skillxOnly, setSkillxOnly] = useState(false);
  const [tab, setTab] = useState('all');
  const [employeeId, setEmployeeId] = useState('');
  const [search, setSearch] = useState('');
  const [hideIdle, setHideIdle] = useState(false);
  // CROs are grouped under their CRM (reporting head); off = one flat sheet-style list
  // 'team' = CROs under their CRM (sorting orders teams, then CROs inside a team);
  // 'flat' = one ranked list of every CRO (sorting is individual). Remembered per browser.
  const [viewMode, setViewMode] = useState(() => {
    try {
      return window.localStorage.getItem('croProjectTracker.viewMode') === 'flat' ? 'flat' : 'team';
    } catch (e) {
      return 'team';
    }
  });
  const groupByTeam = viewMode === 'team';
  // Expand (the default) = BOTH tables - the CRO grid and the Accounts Manager's daily table - grow to their full height (no scrolling inside
  // them) and the page scrolls instead, their header and total rows staying in view. Collapse = fixed-height tables that scroll inside.
  // A choice made earlier in this browser is kept; with none, the page opens expanded.
  // Off = a fixed-height table with a frozen header. Remembered per browser.
  const [expanded, setExpanded] = useState(() => readExpanded());
  const toggleExpanded = () =>
    setExpanded((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(EXPANDED_STORAGE_KEY, next ? '1' : '0');
      } catch (err) {
        /* storage blocked - the choice just won't be remembered */
      }
      return next;
    });
  const changeViewMode = (e, next) => {
    if (!next) return; // a toggle group can be deselected; one of the two must stay on
    setViewMode(next);
    try {
      window.localStorage.setItem('croProjectTracker.viewMode', next);
    } catch (err) {
      /* storage blocked - the choice just won't be remembered */
    }
  };
  const [collapsed, setCollapsed] = useState(() => new Set());
  // opens sorted BY PROJECT: all of a project's teams together, then the next project's (see sortRows / groupRows in trackerConfig)
  const [sort, setSort] = useState({ key: 'project', dir: 'asc' });
  const [notesAnchor, setNotesAnchor] = useState(null);
  // Advanced: until HR makes ECPC a department, optionally count the CRA-ECPC wing as its own STC
  // column (the interim calculation). Off by default = the general, department-only way. Remembered.
  const [advancedAnchor, setAdvancedAnchor] = useState(null);
  const [ecpcWing, setEcpcWing] = useState(() => {
    try {
      return window.localStorage.getItem('croProjectTracker.ecpcWing') === '1';
    } catch (e) {
      return false;
    }
  });
  const changeEcpcWing = (e) => {
    const next = e.target.checked;
    setEcpcWing(next);
    try {
      window.localStorage.setItem('croProjectTracker.ecpcWing', next ? '1' : '0');
    } catch (err) {
      /* storage blocked - the choice just won't be remembered */
    }
  };
  const [toast, setToast] = useState('');
  // the number that was clicked: { metric, title, scope | formula } - opens the rows behind it
  const [drill, setDrill] = useState(null);

  const range = useMemo(() => periodRange(selectedMonth, period), [selectedMonth, period]);

  const fetchData = useCallback(
    (hardRefresh = false) => {
      dispatch(
        getCroProjectTracker({
          month: selectedMonth,
          skillx: skillxOnly,
          ecpcWing,
          employeeId,
          fromDate: range.isWeek ? range.fromDate : undefined,
          toDate: range.isWeek ? range.toDate : undefined,
          hardRefresh,
        }),
      );
    },
    [dispatch, selectedMonth, skillxOnly, ecpcWing, employeeId, range],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // The Accounts Manager (department income) of the project departments: whole month, narrowed on the server to what the viewer may see
  const fetchDepartmentIncome = useCallback(
    (hardRefresh = false) => {
      dispatch(getProjectDepartmentIncome({ month: selectedMonth, skillx: skillxOnly, hardRefresh }));
    },
    [dispatch, selectedMonth, skillxOnly],
  );
  useEffect(() => {
    fetchDepartmentIncome();
  }, [fetchDepartmentIncome]);
  // a viewer whose department is not a project one gets no department income at all - then the CRO tracker simply takes the full width
  const showDeptPanel = deptResponse?.meta?.access !== 'none';

  // the per-project summary table can be folded away to give the CRO grid more room; remembered per browser
  const [summaryOpen, setSummaryOpen] = useState(() => {
    try {
      return window.localStorage.getItem('croProjectTracker.summaryOpen') !== '0';
    } catch (e) {
      return true;
    }
  });
  const toggleSummary = () =>
    setSummaryOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('croProjectTracker.summaryOpen', next ? '1' : '0');
      } catch (err) {
        /* storage blocked - the choice just won't be remembered */
      }
      return next;
    });
  // The CRO grid and the Accounts Manager's daily table start on the same line (see the grid areas below) and get the same height: the
  // rest of the window below that line. Measured, because what sits above them (summary open or folded, a TAR row, ...) changes.
  const gridCellRef = useRef(null);
  const [tablesMaxHeight, setTablesMaxHeight] = useState('max(260px, calc(100vh - 640px))');
  useLayoutEffect(() => {
    const cell = gridCellRef.current;
    if (!cell) return undefined;
    const top = () => {
      let t = 0; // where the cell starts on the page: independent of how far the page is scrolled
      for (let node = cell; node; node = node.offsetParent) t += node.offsetTop;
      return t;
    };
    const fit = () => setTablesMaxHeight(`${Math.max(260, Math.round(window.innerHeight - top() - 28))}px`);
    fit();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    // the cell's start moves when the block above it changes height, so watch that block (its parent row's other cells)
    if (observer && cell.parentElement) [...cell.parentElement.children].forEach((child) => observer.observe(child));
    window.addEventListener('resize', fit);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [showDeptPanel, summaryOpen]);

  // the SkillX switch swaps the whole project set, so stale tab/employee choices would match nothing
  useEffect(() => {
    setTab('all');
    setEmployeeId('');
  }, [skillxOnly]);

  const data = responseData?.data || {};
  const projects = data.projects || [];
  const scope = data.scope || {};
  const notes = data.notes || {};
  const employees = scope.employees || [];
  const hasData = projects.length > 0;
  // week view: Target and TAR are monthly numbers, so comparing them to one week would mislead
  const hideTargets = range.isWeek;

  // a project that vanished (scoped user, SkillX swap) must not leave the tab strip on nothing
  useEffect(() => {
    if (tab !== 'all' && !projects.some((p) => p.key === tab)) setTab('all');
  }, [projects, tab]);

  const projectOrder = useMemo(() => projectOrderOf(data.projects || []), [data.projects]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = (data.cros || []).filter(
      (c) =>
        (tab === 'all' || c.projectKey === tab) &&
        (!term || c.name.toLowerCase().includes(term)) &&
        (!hideIdle || hasActivity(c)),
    );
    return sortRows(filtered, sort, projectOrder);
  }, [data.cros, tab, search, hideIdle, sort, projectOrder]);

  const columns = useMemo(() => NUMERIC_COLUMNS.filter((c) => !(hideTargets && c.target)), [hideTargets]);
  const showProject = tab === 'all';

  // the window/filters every drill-down list must share with the table the number came from
  const drillParams = useMemo(
    () => ({
      month: selectedMonth,
      fromDate: range.isWeek ? range.fromDate : undefined,
      toDate: range.isWeek ? range.toDate : undefined,
      skillx: skillxOnly,
      ecpcWing,
      employeeId,
    }),
    [selectedMonth, range, skillxOnly, ecpcWing, employeeId],
  );
  // the Total row sums exactly the rows on screen (after search / hide-no-activity), so its list is
  // scoped to those same people - and to the project tab, when one is selected
  const totalScope = useMemo(
    () => ({ userIds: [...new Set(visibleRows.map((r) => r.userId))], projectKey: tab === 'all' ? undefined : tab }),
    [visibleRows, tab],
  );
  const totalWho = tab === 'all' ? 'All CROs' : `${(projects.find((p) => p.key === tab) || {}).label || ''} CROs`;

  const groups = useMemo(() => (groupByTeam ? groupRows(visibleRows, sort, projectOrder) : null), [groupByTeam, visibleRows, sort, projectOrder]);
  // same order the grid shows (team by team when grouped), so Copy/Export match the screen
  const orderedRows = useMemo(() => (groups ? groups.flatMap((g) => g.rows) : visibleRows), [groups, visibleRows]);
  const toggleGroup = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // ---- Copy (paste straight into Sheets/Excel) and Excel export: same rows, same order as the grid ----
  const tableMatrix = () => {
    const head = ['#', 'CRO', 'Team (CRM)', ...(showProject ? ['Project'] : []), ...columns.map((c) => c.label)];
    const body = orderedRows.map((r, i) => [
      i + 1,
      r.name + (r.isCrm ? ' (CRM own)' : '') + (r.active ? '' : ' (Inactive)'),
      r.teamName || 'No CRM',
      ...(showProject ? [r.project] : []),
      ...columns.map((c) => (c.kind === 'tar' ? (r.tar === null ? '' : Number(r.tar.toFixed(1))) : Math.round(r[c.key]))),
    ]);
    return [head, ...body];
  };

  const handleCopy = async () => {
    const tsv = tableMatrix()
      .map((row) => row.join('\t'))
      .join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      setToast(`Copied ${visibleRows.length} rows - paste into Sheets or Excel`);
    } catch (e) {
      setToast('Copy was blocked by the browser - use Export instead');
    }
  };

  const handleExport = () => {
    const ws = XLSX.utils.aoa_to_sheet(tableMatrix());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Project Tracker');
    const label = period === 'month' ? dayjs(selectedMonth).format('YYYY-MM') : `${dayjs(selectedMonth).format('YYYY-MM')}-${period}`;
    XLSX.writeFile(wb, `project-tracker-${label}${skillxOnly ? '-skillx' : ''}.xlsx`);
  };

  const windowLabel = data.window
    ? `${fmtDay(data.window.fromDate)} – ${fmtDay(data.window.toDate)} ${dayjs(data.window.toDate).format('YYYY')}`
    : `${fmtDay(range.fromDate)} – ${fmtDay(range.toDate)} ${dayjs(range.toDate).format('YYYY')}`;

  const emptyHint = !hasData
    ? 'No data for this month yet.'
    : search || hideIdle || employeeId
      ? 'No CROs match these filters. Clear the search / "Hide no-activity" / employee filter.'
      : 'No CROs in this project for the selected period.';

  return (
    <GlobalWrapper title="Project Tracker">
     <Box sx={{ p: 0.5 }}>
      {/* Four cells in two rows, so the two TABLES always start on the same line whatever the height of what is above them:
            lt = the CRO tracker's controls / KPIs / summary / tabs      ri = the Accounts Manager's figures
            lg = the CRO grid                                            rt = the Accounts Manager's daily table
          Narrow screens stack them in the order lt, lg, ri, rt. */}
      <Box
        sx={{
          display: 'grid',
          gap: '10px 12px',
          alignItems: 'start',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gridTemplateAreas: showDeptPanel ? '"lt" "lg" "ri" "rt"' : '"lt" "lg"',
          [SIDE_BY_SIDE]: {
            gridTemplateColumns: showDeptPanel ? 'minmax(0, 1.7fr) minmax(0, 1fr)' : 'minmax(0, 1fr)',
            gridTemplateAreas: showDeptPanel ? '"lt ri" "lg rt"' : '"lt" "lg"',
          },
          [WIDE_SCREEN]: { gridTemplateColumns: showDeptPanel ? 'minmax(0, 1.4fr) minmax(0, 1fr)' : 'minmax(0, 1fr)' },
        }}
      >
      <Box sx={{ minWidth: 0, gridArea: 'lt' }}>
      {/* ---------- Controls: everything a sheet user would reach for, in one strip ---------- */}
      <Box
        mb={1}
        sx={{ ...PANEL_SX, p: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box sx={{ width: 140 }}>
            <DatePickerCustom
              label="Month"
              value={dayjs(selectedMonth)}
              setValue={(date) => setSelectedMonth(date.format('YYYY-MM-01'))}
              viewDetails={['year', 'month']}
              fullWidth
              dateDisplayFormat="MM-YYYY"
            />
          </Box>

          {/* Week 1-4, like the sheet's Start/End cells */}
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {PERIODS.map((p) => {
              const r = periodRange(selectedMonth, p.key);
              return (
                <Tooltip key={p.key} title={`${fmtDay(r.fromDate)} – ${fmtDay(r.toDate)}`} arrow>
                  <Chip
                    label={p.label}
                    size="small"
                    onClick={() => setPeriod(p.key)}
                    sx={
                      period === p.key
                        ? { fontWeight: 700, color: '#fff', backgroundColor: THEME.primary, '&:hover': { backgroundColor: THEME.primaryDark } }
                        : { fontWeight: 600, color: THEME.primaryDark, backgroundColor: THEME.primarySoft, border: `1px solid ${THEME.primaryLine}`, '&:hover': { backgroundColor: THEME.primaryLine } }
                    }
                  />
                </Tooltip>
              );
            })}
          </Stack>

          <FormControlLabel
            sx={{ m: 0 }}
            control={<Switch size="small" checked={skillxOnly} onChange={(e) => setSkillxOnly(e.target.checked)} />}
            label="SkillX"
          />

          <TextField select size="small" label="Employee" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">All employees</MenuItem>
            {employees.map((e) => (
              <MenuItem key={e.userId} value={e.userId}>
                {e.name}
                {!e.active ? ' (Inactive)' : ''}
              </MenuItem>
            ))}
          </TextField>

          <TextField size="small" label="Search CRO" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: 150 }} />

          <FormControlLabel
            sx={{ m: 0 }}
            control={<Switch size="small" checked={hideIdle} onChange={(e) => setHideIdle(e.target.checked)} />}
            label="Hide no-activity"
          />

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Advanced options">
            <IconButton size="small" onClick={(e) => setAdvancedAnchor(e.currentTarget)}>
              <TuneRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reload (skips the 1-minute cache)">
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  fetchData(true);
                  fetchDepartmentIncome(true);
                }}
                disabled={loading || deptLoading}
              >
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyRoundedIcon />}
            onClick={handleCopy}
            disabled={!visibleRows.length}
          >
            Copy
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            onClick={handleExport}
            disabled={!visibleRows.length}
          >
            Export
          </Button>
        </Stack>

        {/* What am I looking at? - the sheet makes you hunt for its Start/End cells */}
        <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ color: THEME.primaryDark }}>
            <b>{windowLabel}</b>
            {range.isWeek ? ` · ${PERIODS.find((p) => p.key === period).label}` : ' · Full month'}
            {skillxOnly ? ' · SkillX only' : ''}
            {ecpcWing && !skillxOnly ? ' · ECPC wing shown as STC (advanced)' : ''}
            {scope.type === 'hierarchy' ? ' · You and your reporting team' : ''}
            {hideTargets ? ' · Targets are monthly, so Target/TAR are hidden in a week view' : ''}
          </Typography>
          <Tooltip title="How these numbers are worked out" arrow>
            <IconButton size="small" onClick={(e) => setNotesAnchor(e.currentTarget)}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Stack direction="row" spacing={0.5} sx={{ ml: 'auto !important' }}>
            {!hideTargets &&
              TAR_BANDS.map((b) => (
                <Chip key={b.label} label={b.label} size="small" sx={{ color: b.fg, backgroundColor: b.bg, height: 20, fontSize: 11, fontWeight: 700 }} />
              ))}
          </Stack>
        </Stack>
      </Box>

      <Popover
        open={Boolean(advancedAnchor)}
        anchorEl={advancedAnchor}
        onClose={() => setAdvancedAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 360 }}>
          <Typography variant="subtitle2" gutterBottom>
            Advanced
          </Typography>
          <FormControlLabel
            sx={{ m: 0, alignItems: 'flex-start' }}
            control={<Switch size="small" checked={ecpcWing} onChange={changeEcpcWing} disabled={skillxOnly} sx={{ mt: 0.25 }} />}
            label={
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Show the ECPC wing as its own department (STC)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ECPC is not a department yet, so by default its people are counted in Little Genie. Turn this on to split the CRA-ECPC wing out as an STC column
                  for now. Once HR creates an ECPC department, the STC column appears by itself and this switch is no longer needed.
                </Typography>
              </Box>
            }
          />
          {data.config && data.config.ecpcDepartmentFound && (
            <Alert severity="success" sx={{ mt: 1.5, py: 0 }}>
              An ECPC / STC department exists - the STC column is on automatically.
            </Alert>
          )}
          {skillxOnly && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Not used in the SkillX view.
            </Typography>
          )}
        </Box>
      </Popover>

      <Popover
        open={Boolean(notesAnchor)}
        anchorEl={notesAnchor}
        onClose={() => setNotesAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 460 }}>
          <Typography variant="subtitle2" gutterBottom>
            How to read this
          </Typography>
          {columns.map((c) => (
            <Typography key={c.key} variant="body2" sx={{ mb: 0.5 }}>
              <b>{c.label}</b> — {c.tip}
            </Typography>
          ))}
          {notes.teams && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {notes.teams}
            </Typography>
          )}
          {notes.targets && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {notes.targets} Sales TAR / Fee TAR are hidden until separate targets exist.
            </Typography>
          )}
          {notes.ogdStc && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {notes.ogdStc}
            </Typography>
          )}
        </Box>
      </Popover>

      {/* A failed request must never look like "no data" */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={() => fetchData(true)}>
              Retry
            </Button>
          }
        >
          Could not load the Project Tracker: {error}
        </Alert>
      )}

      {!error && (
        <>
          {/* ---------- Headline numbers, then a column per project (tap a project to jump into its CROs) ---------- */}
          {hasData && (
            <Box mb={1}>
              <KpiCards projects={projects} showTar={!hideTargets} onDrill={setDrill} />
            </Box>
          )}
          {hasData && (
            <Box mb={1}>
              <Box
                onClick={toggleSummary}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSummary();
                  }
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: THEME.primaryDark, fontWeight: 700, fontSize: 13, mb: summaryOpen ? 0.5 : 0, userSelect: 'none' }}
              >
                {summaryOpen ? <KeyboardArrowUpRoundedIcon fontSize="small" /> : <KeyboardArrowDownRoundedIcon fontSize="small" />}
                Project summary
                <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {summaryOpen ? '(click to fold)' : '(click to open)'}
                </Typography>
              </Box>
              {summaryOpen && (
                <SummaryTable
                  dense
                  onDrill={setDrill}
                  projects={projects}
                  showOgd={scope.ogdVisible !== false && !skillxOnly}
                  activeProject={tab}
                  onPickProject={(key) => setTab(key)}
                />
              )}
            </Box>
          )}

          {/* ---------- One grid, a tab per project (each tab = that project's block with its total) ---------- */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
          <ProjectTabs value={tab} onChange={setTab} allCount={croCount(data.cros || [])} projects={projects} />
          {/* the view buttons: beside the tabs when there is room, on a row of their own (right-aligned) when there is not */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 auto', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={changeViewMode}
            sx={{
              flexShrink: 0,
              '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, fontSize: 12.5, px: 1, color: THEME.primaryDark, borderColor: THEME.primaryLine },
              '& .Mui-selected': { color: '#fff !important', backgroundColor: `${THEME.primary} !important` },
            }}
          >
            <Tooltip title="CROs under their CRM. Sorting orders the teams, then the CROs inside each team." arrow>
              <ToggleButton value="team">
                <GroupsRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
                By team
              </ToggleButton>
            </Tooltip>
            <Tooltip title="One ranked list of every CRO. Sorting compares individuals across all teams." arrow>
              <ToggleButton value="flat">
                <FormatListNumberedRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
                Rank everyone
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          <Tooltip title={expanded ? 'Back to fixed-height tables that scroll inside, with a frozen header' : 'Show every row of both tables at full height - no scrolling inside them'} arrow>
            <ToggleButton
              value="expanded"
              size="small"
              selected={expanded}
              onChange={toggleExpanded}
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                fontWeight: 700,
                px: 1,
                color: THEME.primaryDark,
                borderColor: THEME.primaryLine,
                '&.Mui-selected': { color: '#fff', backgroundColor: THEME.primary, '&:hover': { backgroundColor: THEME.primaryDark } },
              }}
            >
              {expanded ? <UnfoldLessRoundedIcon fontSize="small" sx={{ mr: 0.5 }} /> : <UnfoldMoreRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />}
              {expanded ? 'Collapse' : 'Expand'}
            </ToggleButton>
          </Tooltip>
          </Box>
          </Box>
        </>
      )}
      </Box>

      <Box ref={gridCellRef} sx={{ minWidth: 0, gridArea: 'lg' }}>
      {!error && (
          <CroGrid
            rows={visibleRows}
            expanded={expanded}
            maxHeight={tablesMaxHeight}
            onDrill={setDrill}
            totalScope={totalScope}
            totalWho={totalWho}
            groups={groups}
            collapsed={collapsed}
            onToggleGroup={toggleGroup}
            sort={sort}
            onSort={(key) => setSort((s) => nextSort(s, key))}
            showProject={showProject}
            hideTargets={hideTargets}
            loading={loading}
            emptyHint={emptyHint}
          />
      )}

      <DetailDialog request={drill} params={drillParams} onClose={() => setDrill(null)} />

      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
      </Box>

      {/* ---------- Accounts Manager of the project departments (whole month) ---------- */}
      {showDeptPanel && (
        <>
          {/* ri: side by side this cell is exactly as tall as the CRO tracker's top block beside it (the figures are laid over it and scroll
              on their own if they do not fit), so the daily table below it starts on the same line as the CRO grid */}
          <Box sx={{ minWidth: 0, gridArea: 'ri', [SIDE_BY_SIDE]: { position: 'relative', alignSelf: 'stretch', minHeight: 360 } }}>
            <Box sx={{ [SIDE_BY_SIDE]: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', overflowX: 'hidden', pr: 0.5 } }}>
              <ProjectDepartmentInfo
                responseData={deptResponse}
                loading={deptLoading}
                error={deptError}
                month={selectedMonth}
                skillxOnly={skillxOnly}
                onReload={() => fetchDepartmentIncome(true)}
              />
            </Box>
          </Box>
          <Box sx={{ minWidth: 0, gridArea: 'rt' }}>
            <ProjectDepartmentTable
              responseData={deptResponse}
              loading={deptLoading}
              error={deptError}
              month={selectedMonth}
              skillxOnly={skillxOnly}
              maxHeight={tablesMaxHeight}
              expanded={expanded}
            />
          </Box>
        </>
      )}
      </Box>
     </Box>
    </GlobalWrapper>
  );
}
